import { Injectable } from '@angular/core';
import { ApiService } from '@/core/services/open-api/api.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CurrentUser, SubuserCreateRequestDto, User, UserCreateRequestDto } from '@/core/models/user';
import { CurrentUserForm } from '@/core/models/dtos';
import { map } from 'rxjs/operators';
import { Account, AccountFormDto, AccountSettingsForm, UserForm, UserUpdateForm } from '@/core/models/account';
import { LogoGetDto, LogoMetadataDto } from '@/core/models/logo-dtos';
import { UserState } from '@/core/models/user-state';

/**
 * @class AccountRepository
 * @description Encapsulates raw API communication for account and user data.
 */
@Injectable({ providedIn: 'root' })
export class AccountRepository extends ApiService {
    constructor(http: HttpClient) {
        super(http);
    }

    /**
     * Fetches the current user from the API.
     * @returns Observable of the current user (CurrentUser model class).
     */
    public getCurrentUser(): Observable<CurrentUser> {
        return this._http.get<CurrentUserForm>(ApiService.openApiUrl + 'account/me').pipe(
            map((responseDto: CurrentUserForm) => {
                return CurrentUser.fromJson(responseDto);
            }),
        );
    }

    updateCurrentUserForm(currentUser: UserUpdateForm): Observable<CurrentUserForm> {
        return this._http.put<CurrentUserForm>(ApiService.openApiUrl + 'account/me', currentUser);
    }

    /**
     * Fetches the current user's account from the API.
     * Users in state "Deleted" are filtered serverside and are never part of the response.
     * @returns Observable of the current user's account (Account model class).
     */
    public getAccount(): Observable<Account> {
        return this._http.get<AccountFormDto>(ApiService.privateApiUrl + 'account').pipe(
            map((response: AccountFormDto): Account => {
                return Account.fromApi(response);
            }),
        );
    }

    // Note: The is actually using put, but has a patch logic. That is why this method is called patch. Put would expect a full object.
    public patchAccountSettings(settings: Partial<AccountSettingsForm>): Observable<void> {
        return this._http.put<void>(ApiService.privateApiUrl + 'account/settings', settings);
    }

    public createSubuser(userCreateDto: SubuserCreateRequestDto): Observable<User> {
        return this._http.post<UserForm>(ApiService.privateApiUrl + 'account/user', userCreateDto).pipe(
            map((response: UserForm) => {
                return User.fromJson(response);
            }),
        );
    }

    public patchSubuser(userId: number, changes: UserUpdateForm): Observable<User> {
        return this._http.put<UserForm>(ApiService.privateApiUrl + 'account/user/' + userId, changes).pipe(
            map((response: UserForm) => {
                return User.fromJson(response);
            }),
        );
    }

    /**
     * Changes nothing but the state of the given user. The endpoint is a PUT, but absent fields keep
     * their stored value, so sending the state alone skips the server side name validation (#747).
     */
    public updateSubuserState(userId: number, state: UserState): Observable<User> {
        return this.patchSubuser(userId, { state });
    }

    /**
     * Deletes the user with the specified id of this account.
     * @param userId
     */
    public deleteUser(userId: number | undefined): Observable<void> {
        return this._http.delete<void>(ApiService.privateApiUrl + 'account/user/' + userId);
    }

    // specific functions for account settings:

    /**
     * This method returns a link to the store system containing a token for authentication.
     * Each time when the endpoint is called, a new token is generated.
     * @returns Observable of the link to the store system including the token.
     */
    public getLicensesURL(): Observable<string> {
        return this._http.post<string>(ApiService.privateApiUrl + 'account/subscription/link', {});
    }

    public createAPIKey(): Observable<string> {
        return this._http.post<string>(ApiService.privateApiUrl + 'account/apikey', {});
    }

    public deleteAPIKey(): Observable<void> {
        return this._http.delete<void>(ApiService.privateApiUrl + 'account/apikey');
    }

    public startTrial(date: string): Observable<void> {
        return this._http.post<void>(ApiService.privateApiUrl + 'account/trial', { endDate: date });
    }

    //  account activation functions:

    public createAccount(user: UserCreateRequestDto): Observable<User> {
        return this._http
            .post<UserForm>(ApiService.privateApiUrl + 'account/register', user)
            .pipe(map((response) => User.fromJson(response)));
    }

    public activateUser(token: string): Observable<void> {
        return this._http.post<void>(ApiService.privateApiUrl + 'account/activate/' + token, {});
    }

    public requestPasswordReset(mail: string): Observable<void> {
        return this._http.post<void>(ApiService.privateApiUrl + 'account/password/new', { email: mail });
    }

    public sendNewPassword(token: string): Observable<void> {
        return this._http.post<void>(ApiService.privateApiUrl + 'account/password/new/' + token, {});
    }

    public resendActivationCode(token: string): Observable<void> {
        return this._http.post<void>(ApiService.privateApiUrl + 'account/reactivate/' + token, {});
    }

    // logo functions:

    public getReportLogoMetadata(): Observable<LogoMetadataDto> {
        return this._http.get<LogoMetadataDto>(ApiService.privateApiUrl + 'account/v1/storage/logo/metadata');
    }

    public uploadReportLogo(file: File): Observable<void> {
        const formData = new FormData();
        formData.append('file', file);
        return this._http.post<void>(ApiService.privateApiUrl + 'account/v1/storage/logo', formData);
    }

    public fetchReportLogo(): Observable<LogoGetDto> {
        return this._http.get<LogoGetDto>(ApiService.privateApiUrl + 'account/v1/storage/logo');
    }

    public deleteReportLogo(): Observable<void> {
        return this._http.delete<void>(ApiService.privateApiUrl + 'account/v1/storage/logo');
    }
}
